import React, { FC, useEffect, useRef, useState } from 'react';

import { InfoCircleOutlined, LoadingOutlined, StopOutlined } from '@ant-design/icons';
import { Form, Input, Modal, Popover, Select, Space, Spin, Switch } from 'antd';

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
    <Popover
        content={
            <div style={{ maxWidth: 350 }}>
                <p style={{ margin: 0 }}>{text}</p>
                <p style={{ margin: '8px 0 0 0', fontSize: 12 }}>
                    Если непонятно и остались вопросы, напишите нам{' '}
                    <a href="https://t.me/analytics_scan" target="_blank" rel="noopener noreferrer">
                        https://t.me/analytics_scan
                    </a>
                </p>
            </div>
        }
        trigger="click"
        placement="top"
    >
        <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
    </Popover>
);

import styles from './Staff.module.scss';
import { checkValueForStaffForms } from '../../../../helpers/utils/data';
import { showFormErrors } from '../../../../helpers/utils/ui';
import { useActions } from '../../../../shared/common/hooks/useActions';
import { useForceUpdate } from '../../../../shared/common/hooks/useForceUpdate';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';

// Список ролей в алфавитном порядке, "Другое" в конце
const ROLE_OPTIONS = [
    'Агентство по трафику',
    'Аналитик',
    'Бухгалтерия',
    'Владелец школы',
    'Менеджер по продажам',
    'Операционный директор',
    'Руководитель маркетинга',
    'Руководитель продаж',
    'Руководитель трафика',
    'Специалист по маркетингу',
    'Специалист по трафику',
    'Технический специалист',
    'Финансовый директор',
    'Другое',
];

interface IAddModalProps {
    isVisible: boolean
    setVisible: (val: boolean) => void
    userId: number
}

const EditModal: FC<IAddModalProps> = ({ isVisible, setVisible, userId }) => {
    const forceUpdate = useForceUpdate();
    const [ form ] = Form.useForm();
    const modalContentRef = useRef<HTMLDivElement>(null);
    const adsSelectRef = useRef<any>(null);
    const offersSelectRef = useRef<any>(null);

    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const { isLoading, ads_list, order_list, updateData } = useTypedSelector((state) => state.staff);
    const { staffGetUserData, staffGetLists, staffUpdateUser } = useActions();
    const [ isAdmin, setIsAdmin ] = useState<boolean>();

    const [ rkSelectDisable, setRkSelectDisable ] = useState<(number | string)[]>([]);
    const [ offersSelectDisable, setOffersSelectDisable ] = useState<(number | string)[]>([]);
    const [ showUtm, setShowUtm ] = useState(false);
    const [ selectedRole, setSelectedRole ] = useState<string>('');
    const [ additionalRole, setAdditionalRole ] = useState<string>('');
    const [ roleValidationTriggered, setRoleValidationTriggered ] = useState(false);
    useEffect(() => {
        if (isVisible && selectedSchool) {
            form.resetFields();

            staffGetLists(selectedSchool.id, () => {
                staffGetUserData(parseInt(selectedSchool.id), userId);
            });
        }
    }, [ isVisible ]);
    useEffect(() => {
        if (updateData) {
            // Преобразуем ads и offers в массивы, если они не массивы
            // API может вернуть: null, 0, число, или массив
            const normalizeToArray = (val: any): any[] => {
                if (val === null || val === undefined) return [];
                if (Array.isArray(val)) return val;
                // API может вернуть объект вида {"0": id, "2": id, ...} вместо массива
                if (typeof val === 'object') return Object.values(val);
                // Если это число или строка — оборачиваем в массив
                return [val];
            };

            const newValues = Object.entries(updateData).map(([ key, val ]) => {
                if (key === 'ads' || key === 'offers') {
                    return { name: key, value: normalizeToArray(val) };
                }
                return { name: key, value: val };
            });
            
            if(newValues.some((element)=>element.name.includes('utm'))){
                setShowUtm(true);
            }else{
                setShowUtm(false);
            }
            form.setFields(newValues);

            // Инициализируем rkSelectDisable и offersSelectDisable на основе загруженных данных
            const adsValues = normalizeToArray(updateData.ads);
            const offersValues = normalizeToArray(updateData.offers);
            
            setRkSelectDisable(adsValues);
            setOffersSelectDisable(offersValues);
            
            // Принудительно перерисовываем компонент после установки значений
            setTimeout(() => forceUpdate(), 0);

            // Парсим роль из данных
            if (updateData.role) {
                const roleValue = updateData.role as string;
                // Проверяем, есть ли роль из списка
                const parts = roleValue.split(',').map(p => p.trim());
                const foundRole = ROLE_OPTIONS.find(r => parts.includes(r));
                if (foundRole) {
                    setSelectedRole(foundRole);
                    const remaining = parts.filter(p => p !== foundRole).join(', ');
                    setAdditionalRole(remaining);
                } else {
                    // Если роль не из списка, ставим "Другое" и всё в дополнительное поле
                    setSelectedRole('Другое');
                    setAdditionalRole(roleValue);
                }
            } else {
                setSelectedRole('');
                setAdditionalRole('');
            }
        }
    }, [ updateData ]);

    const scrollToTop = () => {
        modalContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const saveStaff = (values: any) => {
        if (selectedSchool) {
            // Объединяем выбранную роль и дополнительный текст
            let roleValue = selectedRole;
            if (additionalRole.trim()) {
                roleValue = roleValue ? `${roleValue}, ${additionalRole.trim()}` : additionalRole.trim();
            }

            // Проверка обязательности поля "Чем занимается"
            if (!selectedRole) {
                setRoleValidationTriggered(true);
                scrollToTop();
                return;
            }

            const parsedData = {
                ...values,
                role: roleValue,
                ads: checkValueForStaffForms(values.ads),
                offers: checkValueForStaffForms(values.offers),
                contact: values.contact || false,
                setting: values.setting || false
            };

            staffUpdateUser(parseInt(selectedSchool.id), userId, parsedData, () => {
                setVisible(false);
                setSelectedRole('');
                setAdditionalRole('');
                setRoleValidationTriggered(false);
            }, (errors) => {
                showFormErrors(form, errors);
            });
        }
    };
    useEffect(() => {
        form.setFieldsValue({ contact: isAdmin });
    }, [ isAdmin ]);
    return (
        <Modal
            title={<span style={{ fontSize: 20, fontWeight: 600 }}>Редактирование сотрудника</span>}
            open={isVisible}
            width={650}
            onOk={() => {
                setRoleValidationTriggered(true);
                form.submit();
            }}
            onCancel={() => {
                setVisible(false);
                setSelectedRole('');
                setAdditionalRole('');
                setRoleValidationTriggered(false);
            }}
            okText="Сохранить"
            cancelText="Отмена"
            okButtonProps={{
                loading: isLoading,
                style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' },
                'data-help-id': 'staff-edit-submit',
            } as any}
            destroyOnClose
            forceRender
        >
            <div ref={modalContentRef}>
            <Spin
                spinning={isLoading}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            >
                <Form
                    form={form}
                    onFinish={saveStaff}
                    onFinishFailed={() => scrollToTop()}
                    layout="vertical"
                    scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                    className={styles.staffForm}
                    initialValues={{
                        rk_access: null,
                        ad_access: null,
                        utm_filter: false,
                        user_contacts_access: false,
                        settings_access: false,
                        utm_source: [],
                        utm_medium: [],
                        utm_campaign: [],
                        utm_term: [],
                        utm_content: [],
                        utm_group: []
                    }}
                    requiredMark={false}
                >

                    <Form.Item
                        label="Имя и Фамилия"
                        name="name"
                        rules={[ { required: true, message: 'Поле обязательное' } ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Чем занимается"
                        required
                        validateStatus={roleValidationTriggered && !selectedRole ? 'error' : ''}
                        help={roleValidationTriggered && !selectedRole ? 'Выберите роль' : ''}
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                style={{ width: '60%' }}
                                placeholder="Выберите роль"
                                showSearch
                                allowClear
                                value={selectedRole || undefined}
                                onChange={(value) => setSelectedRole(value || '')}
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {ROLE_OPTIONS.map((role) => (
                                    <Select.Option key={role} value={role}>
                                        {role}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Input
                                style={{ width: '40%' }}
                                placeholder="Дополнительно"
                                value={additionalRole}
                                onChange={(e) => setAdditionalRole(e.target.value)}
                            />
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[ { required: true, type: 'email', message: 'Введите корректный Email' } ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item 
                        label={
                            <span>
                                Доступ к рекламным кабинетам{' '}
                                <InfoTooltip text="Вы можете задать сотруднику доступ к определённым рекламным кабинетам. Если ничего не выбирать, сотруднику будут доступны ВСЕ рекламные кабинеты. Можно закрыть доступ ко всем рекламным кабинетам. Если сотрудник сам делает интеграцию рекламного кабинета со СКАНом, у него автоматически добавится доступ к подключенному рекламному кабинету. Изменить настройки доступа можно в любой момент." />
                            </span>
                        }
                        name="ads"
                        className={styles.adsSelectVertical}
                    >
                        <Select
                            ref={adsSelectRef}
                            loading={isLoading}
                            mode="multiple"
                            placeholder="Доступны все РК"
                            filterOption={(inputValue, option) => {
                                if (!option) return false;
                                const searchValue = inputValue.toLowerCase().trim();
                                if (!searchValue) return true;
                                // Ищем по label (основной поиск)
                                if (option.label) {
                                    const labelText = String(option.label).toLowerCase();
                                    if (labelText.includes(searchValue)) return true;
                                }
                                // Ищем по children (текст опции)
                                if (option.children) {
                                    let childrenText = '';
                                    if (typeof option.children === 'string') {
                                        childrenText = option.children;
                                    } else if (Array.isArray(option.children)) {
                                        childrenText = option.children
                                            .map((c: any) => typeof c === 'string' ? c : '')
                                            .join(' ');
                                    }
                                    if (childrenText.toLowerCase().includes(searchValue)) return true;
                                }
                                return false;
                            }}
                            onSelect={(value) => {
                                setRkSelectDisable([ ...rkSelectDisable, value ]);
                                adsSelectRef.current?.blur();
                                forceUpdate();
                            }}
                            onDeselect={(value) => {
                                setRkSelectDisable(rkSelectDisable.filter((item) => item !== value));
                                forceUpdate();
                            }}
                            onClear={() => {
                                setRkSelectDisable([]);
                                forceUpdate();
                            }}
                            allowClear
                            style={{ width: '100%' }}
                            dropdownMatchSelectWidth={false}
                            tagRender={(props) => {
                                const { label, closable, onClose } = props;
                                return (
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#f3f3f3',
                                            border: '1px solid #f3f3f3',
                                            borderRadius: 16,
                                            padding: '4px 12px',
                                            marginRight: 30,
                                            marginBottom: 4,
                                            width: '100%',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <span style={{ marginRight: 8, flex: 1, color: '#595959' }}>{label}</span>
                                        {closable && (
                                            <span 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    onClose(e);
                                                }} 
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                                style={{ cursor: 'pointer', color: '#595959', fontSize: 14 }}
                                            >
                                                ×
                                            </span>
                                        )}
                                    </span>
                                );
                            }}
                        >
                            <Select.Option
                                value={0}
                                disabled={rkSelectDisable.some((item) => item !== 0 && String(item) !== '0')}
                            >
                                <StopOutlined style={{ color: '#ff4d4f', marginRight: 5 }} /> Нет доступа к РК
                            </Select.Option>
                            {ads_list.map((item) => {
                                return (
                                    <Select.Option
                                        key={item.id}
                                        value={item.id}
                                        label={item.name}
                                        disabled={rkSelectDisable.some((v) => v === 0 || String(v) === '0')}
                                    >
                                        {item.name}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>


                    <Form.Item
                        label={
                            <span>
                                Показывать заказы определённых предложений{' '}
                                <InfoTooltip text="Вы можете задать доступ к статистике продаж определённых предложений. Если ничего не выбирать, в отчётах будет выводиться вся информация по всем продажам. Если указать определённое предложение, то в отчётах будут продажи только выбранного предложения. Можно запретить вывод продаж по всем предложениям. Настройки можно изменить в любой момент." />
                            </span>
                        }
                        name="offers"
                        className={styles.adsSelectVertical}
                    >
                        <Select
                            ref={offersSelectRef}
                            loading={isLoading}
                            mode="multiple"
                            placeholder="Доступны все предложения"
                            onSelect={(value) => {
                                setOffersSelectDisable([ ...offersSelectDisable, value ]);
                                forceUpdate();
                                offersSelectRef.current?.blur();
                            }}
                            onDeselect={(value) => {
                                setOffersSelectDisable(offersSelectDisable.filter((item) => item !== value));
                                forceUpdate();
                            }}
                            onClear={() => {
                                setOffersSelectDisable([]);
                                forceUpdate();
                            }}
                            filterOption={(inputValue, option) => {
                                if (!option) return false;
                                const searchValue = inputValue.toLowerCase().trim();
                                if (!searchValue) return true;
                                if (option.label) {
                                    const labelText = String(option.label).toLowerCase();
                                    if (labelText.includes(searchValue)) return true;
                                }
                                if (option.children) {
                                    let childrenText = '';
                                    if (typeof option.children === 'string') {
                                        childrenText = option.children;
                                    } else if (Array.isArray(option.children)) {
                                        childrenText = option.children
                                            .map((c: any) => typeof c === 'string' ? c : '')
                                            .join(' ');
                                    }
                                    if (childrenText.toLowerCase().includes(searchValue)) return true;
                                }
                                return false;
                            }}
                            allowClear
                            style={{ width: '100%' }}
                            dropdownMatchSelectWidth={false}
                            tagRender={(props) => {
                                const { label, closable, onClose } = props;
                                return (
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#f3f3f3',
                                            border: '1px solid #f3f3f3',
                                            borderRadius: 16,
                                            padding: '4px 12px',
                                            marginRight: 30,
                                            marginBottom: 4,
                                            width: '100%',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <span style={{ marginRight: 8, flex: 1, color: '#595959' }}>{label}</span>
                                        {closable && (
                                            <span 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    onClose(e);
                                                }} 
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                                style={{ cursor: 'pointer', color: '#595959', fontSize: 14 }}
                                            >
                                                ×
                                            </span>
                                        )}
                                    </span>
                                );
                            }}
                        >
                            <Select.Option
                                value={0}
                                disabled={offersSelectDisable.some((item) => item !== 0 && String(item) !== '0')}
                            >
                                <StopOutlined style={{ color: '#ff4d4f', marginRight: 5 }} /> Нет доступа к предложениям
                            </Select.Option>
                            {
                                order_list.map((item) => {
                                    return (
                                        <Select.Option
                                            key={item.id}
                                            value={item.id}
                                            label={item.name}
                                            disabled={offersSelectDisable.some((v) => v === 0 || String(v) === '0')}
                                        >
                                            {item.name}
                                        </Select.Option>
                                    );
                                })
                            }
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                Права администратора{' '}
                                <InfoTooltip text="Это позволит сотруднику иметь доступ ко всем настройкам сервиса и выполнять любые действия." />
                            </span>
                        }
                        name="setting"
                        valuePropName="checked"
                    >
                        <Switch  onChange={(checked)=>(setIsAdmin(checked))}/>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                Показать контакты пользователей{' '}
                                <InfoTooltip text="Если включить, сотруднику будут доступны все таблицы с контактными данными ваших учеников из базы данных GetCourse (пользователи, регистрации, заказы, платежи). Также в отчётах будет доступен вывод email и телефон. По умолчанию у всех доступ к контактам отключен. Сотрудник будет видеть статистику обезличенных данных." />
                            </span>
                        }
                        name="contact"
                        valuePropName="checked"
                    >
                        <Switch/>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                Фильтровать регистрации по UTM меткам{' '}
                                <InfoTooltip text="Вы можете сделать так, чтобы каждый сотрудник видел данные только по своему рекламному трафику. Нужно для каждого сотрудника задать одну уникальную UTM метку на весь проект или набор UTM меток. Это позволит в личном кабинете сотрудника автоматически отфильтровать только его привлечённый трафик. У него не будет доступа к другим источникам трафика. В одну UTM метку можно добавить несколько меток. Несколько меток в одном поле между собой взаимодействуют через ИЛИ. Метки между разными полями — через оператор И. Изменить настройки можно в любой момент." />
                            </span>
                        }
                    >
                        <Switch
                            checked={showUtm}
                            onChange={(checked) => setShowUtm(checked)}
                        />
                    </Form.Item>

                    {
                        showUtm &&
                        <>
                            <Form.Item
                                label="Метки utm_source"
                                name="utm_source"
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item
                                label="Метки utm_medium"
                                name="utm_medium"
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item
                                label="Метки utm_campaign"
                                name="utm_campaign"
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item
                                label="Метки utm_term"
                                name="utm_term"
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item
                                label="Метки utm_content"
                                name="utm_content"
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item
                                label="Метки utm_group"
                                name="utm_group"
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                />
                            </Form.Item>
                        </>
                    }

                </Form>
            </Spin>
            </div>
        </Modal>
    );
};

export default EditModal;
